// libraries
import type { Incentive, StateTree } from '@makemydeal/dr-dash-types';

// selectors
import {
    getCurrentOfferType,
    getAllIncentiveTotalByOfferType,
    getManualIncentiveRebatesByOfferType
} from '../selectors/offerRedux';
import { DateTime } from 'luxon';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as offerReduxSelectors from '../selectors/offerRedux';
import type { OfferType } from '@makemydeal/dr-platform-types';

/**
 * Resolves the incentive total to persist.
 *
 * The legacy chain returns whichever term is first non-zero. Manual rebates and the program display
 * total are disjoint populations, so a deal carrying both persisted the manual total alone and
 * dropped every program incentive (DE761820). Under restoration this reuses the resolver Manager
 * View displays through, so the saved total and the rendered total cannot disagree. Summing the two
 * is deliberately avoided: an overridden program incentive lives in the manual bag and the catalog
 * at once, and would double-count (DE756526).
 *
 * @param state
 * @param offerType
 * @param appliedIncentiveAmount Program display total, used only by the legacy chain.
 * @returns
 */
const resolveIncentivesTotal = (state: StateTree, offerType: OfferType, appliedIncentiveAmount?: number): number => {
    if (featureToggleSelectors.getFeatureToggle(state, 'enableProgramIncentivesRestoration_F292564')) {
        return offerReduxSelectors.getAppliedIncentivesTotalForDisplay(state, offerType);
    }
    return (
        getManualIncentiveRebatesByOfferType(state, offerType) ||
        appliedIncentiveAmount ||
        offerReduxSelectors.getAppliedIncentivesTotalForNonDealerCash(state)
    );
};

/**
 * Prepares incentives data specifically for AIO
 * @param state
 * @param selectedOfferType
 * @returns
 */
export const buildIncentivesNode = (state: StateTree, selectedOfferType?: OfferType) => {
    const offerType = selectedOfferType ?? getCurrentOfferType(state);
    const enableAppliedLenderInfoToggle = featureToggleSelectors.getFeatureToggle(state, 'enableAppliedLenderInfo');
    const creditDecisionEnabled = offerReduxSelectors.getIsUserProgramQuotes(state);

    const appliedIncentiveAmount =
        enableAppliedLenderInfoToggle && creditDecisionEnabled
            ? offerReduxSelectors.getIncentivesTotalOverrideByOfferType(state, offerType)
            : getAllIncentiveTotalByOfferType(state, offerType);
    const appliedIncentives = offerReduxSelectors.getAppliedIncentivesMappedForAIO(state, selectedOfferType);

    return {
        appliedIncentiveAmount: resolveIncentivesTotal(state, offerType, appliedIncentiveAmount),
        appliedIncentives
    };
};

/**
 * This filter will return false for invalid dates or dates older than seven days local time. Assumes dates are in M/d/yyyy format.
 * @returns
 */
export const filterByEndDate =
    (limitISO: string) =>
    (incentive: { endDate: string | null | undefined }): boolean => {
        const lowerLimit = DateTime.fromISO(limitISO);
        const endValue = incentive.endDate;

        // returning early here because DateTime silently accepts blank but
        //   reports invalid. We want to keep null or blank end dates
        if (endValue == null || endValue === '') return true;
        let end = DateTime.fromFormat(endValue, 'M/d/yyyy');

        if (!end.isValid) {
            end = DateTime.fromISO(endValue);
        }

        if (!end.isValid) {
            return false; // If still not valid, exclude this data point
        }
        // DateTime has custom operators to handle logical comparisons between dates.
        return end >= lowerLimit; // NOTE: valid if more recent than lower limit
    };

/**
 * AMD relies on M/d/yyyy format for dates, so rather than chaning it to ISO [for now], we'll format our incentives in dash to match. The filter also relies on this format.
 * @param incentive
 * @returns
 */
export const transformIncentiveDates = (incentive: Incentive): Incentive => {
    let startDate: DateTime | undefined = DateTime.fromISO(incentive.startDate);
    let endDate: DateTime | undefined = DateTime.fromISO(incentive.endDate);

    if (!startDate.isValid) startDate = undefined;
    if (!endDate.isValid) endDate = undefined;
    return {
        ...incentive,
        endDate: endDate?.toLocaleString(DateTime.DATE_SHORT, { locale: 'en-US' }) ?? '',
        startDate: startDate?.toLocaleString(DateTime.DATE_SHORT, { locale: 'en-US' }) ?? ''
    };
};

// @note not using UTC: intentional. In browsers, this will be local time; start of day
export const getIncentiveActiveLowerLimit = (): string => DateTime.now().startOf('day').minus({ days: 7 }).toISO() as string;

const OFFER_TYPE_TO_FINANCE_METHODS: Record<string, string[]> = {
    cash: ['Cash', 'AllMethodsApply'],
    finance: ['Finance', 'AllMethodsApply'],
    lease: ['Lease', 'AllMethodsApply']
};

export const filterByFinanceMethod =
    (offerType: OfferType) =>
    (incentive: Incentive): boolean =>
        (OFFER_TYPE_TO_FINANCE_METHODS[offerType] ?? []).includes(incentive.financeMethod);

export const filterActiveIncentives = (incentives: Incentive[], offerType: OfferType): Incentive[] =>
    incentives
        .map(transformIncentiveDates)
        .filter(filterByEndDate(getIncentiveActiveLowerLimit()))
        .filter(filterByFinanceMethod(offerType));
